import {
  fetchDocumentPartChildrenWithPermissions,
  fetchDocumentPartDetails,
  fetchDocumentPartNodeWithPermissions,
} from "../../api/document/DocumentsPart";
import { DocumentRoot } from "../../api/models/document-root";
import { EmbeddedRoots } from "../../api/models/embedded-roots";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { TreeViewDataItem } from "./DocumentPartTree";
import { DocumentPosition } from "../../api/models/document-position";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { mapFormToDataItem } from "./DocumentPartTreeUtils";

export const copyItemsAndItem = (
  items: TreeViewDataItem[],
  indexes: number[]
): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
  const copiedItems = items.slice();
  const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

  if (indexes.length > 1) {
    const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
    item.items = children;
    return { items: copiedItems, item: target };
  } else {
    return { items: copiedItems, item: item };
  }
};

function getPaginatedIndex(id: string, page: number) {
  let pos = id.lastIndexOf("_");
  let showMorePosition = page * 20;
  if (pos < 0) {
    return "0_" + showMorePosition;
  } else {
    return id + "_" + showMorePosition;
  }
}

export const resolveExpansionIndexes = (hierarchicalIndex: string, page: number): number[] => {
  const indexes = hierarchicalIndex.split("_").map((value) => Number(value)) ?? [];
  if (page !== 0) {
    indexes.pop();
  }
  return indexes;
};

// Performs only the network part of a node expansion: fetches the children of `nodeKey` and
// notifies the caller about the parts whose status has to be resolved. It intentionally does NOT
// touch the tree, so the caller can apply the result on the freshest tree state (see
// applyFetchedChildren). Keeping fetch and apply separate is what makes concurrent expansions of
// different nodes safe: each response is merged into the current tree instead of overwriting it
// with a stale snapshot.
export const fetchNodeChildren = async (
  nodeKey: string,
  page: number,
  isRootNode: boolean,
  rootData: TreeViewDataItem["data"] | undefined,
  setElementToUpdate: (value: ((prevState: DocumentRoot[]) => DocumentRoot[]) | DocumentRoot[]) => void
): Promise<{ dataPart: EmbeddedRoots; childrenDocumentPart: DocumentRoot[] }> => {
  const dataPart = await fetchDocumentPartNodeWithPermissions(nodeKey, page, {
    params: { notShowLoading: true },
  });
  const childrenDocumentPart: DocumentRoot[] = dataPart._embedded?.content ?? [];

  if (isRootNode && rootData) {
    const documentRoot: DocumentRoot = {
      documentPart: rootData.documentPart ?? rootData,
      hasReadPermissions: false,
    };
    setElementToUpdate(() => [...childrenDocumentPart, documentRoot]);
  } else {
    setElementToUpdate(() => [...childrenDocumentPart]);
  }

  return { dataPart, childrenDocumentPart };
};

// Merges already fetched children into `treeItems` at `indexes`, returning a new tree.
export const applyFetchedChildren = (
  treeItems: TreeViewDataItem[],
  indexes: number[],
  page: number,
  dataPart: EmbeddedRoots,
  childrenDocumentPart: DocumentRoot[]
): TreeViewDataItem[] => {
  const { items, item } = copyItemsAndItem(treeItems, indexes);
  if (!item) return treeItems;

  if (page !== 0) {
    item.expanded = true;
    item.data.subparts = item.data.subparts.concat(childrenDocumentPart);
    item.items.pop();
    item.items = item.items.concat(childrenDocumentPart.map(mapFormToDataItem));
  } else {
    item.expanded = true;
    item.data.subparts = childrenDocumentPart;
    item.items = childrenDocumentPart.map(mapFormToDataItem);
  }

  if (dataPart.page.totalPages != page + 1 && dataPart._embedded?.content) {
    item.items.push({
      text: formatMessage("cof.documentslist.showmore"),
      key: "key",
      hasChildren: false,
      nodeType: "pagination",
      data: { ...dataPart.page, idParent: item.key },
    });
  }

  item.hasChildren = false; // hasChildren is need only for lazy loading
  return items;
};

export const fetchExpandedNodeExternal = async (
  hierarchicalIndexToExpand: string,
  isReload: boolean,
  page: number,
  isInitialization: boolean,
  originalTreeItems: TreeViewDataItem[],
  setElementToUpdate: (value: ((prevState: DocumentRoot[]) => DocumentRoot[]) | DocumentRoot[]) => void
) => {
  let hierarchicalIndex = hierarchicalIndexToExpand;
  if (hierarchicalIndex && isInitialization && page !== 0) {
    hierarchicalIndex = getPaginatedIndex(hierarchicalIndex, page);
  }
  if (!hierarchicalIndex?.length) return [];
  const indexes = resolveExpansionIndexes(hierarchicalIndex, page);
  const { items, item } = copyItemsAndItem(originalTreeItems, indexes);
  item.expanded = isReload ? true : !item.expanded;
  if (!isReload && item.items && page === 0) {
    return items;
  }
  const { dataPart, childrenDocumentPart } = await fetchNodeChildren(
    item.key,
    page,
    hierarchicalIndex === "0",
    originalTreeItems[0]?.data,
    setElementToUpdate
  );

  return applyFetchedChildren(items, indexes, page, dataPart, childrenDocumentPart);
};

export const fetchInitialDocumentPartTreeProps = async (
  initialTreeItem: FlatPathTreeItem,
  rootId: string,
  parentsPositions: DocumentPosition[],
  setElementToUpdate: (value: ((prevState: DocumentRoot[]) => DocumentRoot[]) | DocumentRoot[]) => void
) => {
  if (!rootId.length) return;

  const levels = (parentsPositions?.length ?? 0) + 1;

  const hierarchicalIndexes: string[] = ["0"];
  for (let i = 0; i < levels - 1; i++) {
    hierarchicalIndexes.push(`${hierarchicalIndexes[i]}_${parentsPositions[i].order}`);
  }

  const parentIds: string[] = [];
  for (let level = 0; level < levels; level++) {
    const parentId = level === 0 ? initialTreeItem.document.id : parentsPositions[level - 1].idFiglio;
    parentIds.push(parentId);
  }

  const requestItems: { parentId: string; anchorChildId?: string }[] = [];
  for (let level = 0; level < levels; level++) {
    const parentId = level === 0 ? initialTreeItem.document.id : parentsPositions[level - 1].idFiglio;
    const anchorChildId = level < levels - 1 ? parentsPositions[level].idFiglio : undefined;
    requestItems.push({ parentId, anchorChildId });
  }

  const [documentDetails, bulkResults] = await Promise.all([
    fetchDocumentPartDetails(initialTreeItem.document.id, {
      params: { notShowLoading: true },
    }),
    fetchDocumentPartChildrenWithPermissions(requestItems),
  ]);

  initialTreeItem.document = documentDetails;

  let treeItems: TreeViewDataItem[] = [
    {
      text: initialTreeItem.document.name,
      key: initialTreeItem.document.id,
      hasChildren: initialTreeItem.document.hasChildren,
      nodeType: "part",
      expanded: false,
      items: undefined,
      data: initialTreeItem.document,
      hasReadPermissions: initialTreeItem.readPermission,
    },
  ];

  const allAccumulatedChildren: DocumentRoot[] = [];

  for (let level = 0; level < levels; level++) {
    const indexes = hierarchicalIndexes[level].split("_").map((v) => Number(v));
    const { items: updatedItems, item } = copyItemsAndItem(treeItems, indexes);
    treeItems = updatedItems;

    const parentId = parentIds[level];
    const page = bulkResults[parentId];
    const content = page?._embedded?.content ?? [];

    item.data.subparts = content;
    item.items = content.map(mapFormToDataItem);

    item.expanded = true;
    item.hasChildren = false;

    if (page && page.page && page.page.number + 1 < page.page.totalPages) {
      item.items.push({
        text: formatMessage("cof.documentslist.showmore"),
        key: "key",
        hasChildren: false,
        nodeType: "pagination",
        data: { ...page.page, idParent: item.key },
      });
    }

    if (level === 0) {
      const rootData = treeItems[0].data;
      allAccumulatedChildren.push({ documentPart: rootData.documentPart ?? rootData, hasReadPermissions: false });
    }
    allAccumulatedChildren.push(...content);
  }

  setElementToUpdate(() => allAccumulatedChildren);

  return treeItems;
};
